import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CalendarCheck, Download, ExternalLink, Share2, Link2, Check } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";

function toCalDate(d) {
  const base = new Date(d + "T00:00:00");
  if (isNaN(base)) return null;
  return `${base.getFullYear()}${String(base.getMonth()+1).padStart(2,"0")}${String(base.getDate()).padStart(2,"0")}`;
}
function googleCalUrl(ev) {
  const start = toCalDate(ev.date); if (!start) return null;
  const params = new URLSearchParams({ action:"TEMPLATE", text:ev.title, dates:`${start}/${start}`, details:ev.description||ev.desc||"", location:ev.venue?`${ev.venue}, ${ev.city}`:ev.city||"" });
  return `https://calendar.google.com/calendar/render?${params}`;
}
function downloadIcs(ev) {
  const start = toCalDate(ev.date); if (!start) { toast.error("Invalid date"); return; }
  const stamp = new Date().toISOString().replace(/[-:]/g,"").split(".")[0]+"Z";
  const esc = s => (s||"").replace(/\n/g,"\\n").replace(/,/g,"\\,");
  const ics = ["BEGIN:VCALENDAR","VERSION:2.0","PRODID:-//NC Desi//EN","BEGIN:VEVENT",`UID:${ev.id}@ncdesi`,`DTSTAMP:${stamp}`,`DTSTART;VALUE=DATE:${start}`,`SUMMARY:${esc(ev.title)}`,`DESCRIPTION:${esc(ev.description||ev.desc)}`,`LOCATION:${esc(ev.venue?ev.venue+", "+ev.city:ev.city)}`,`END:VEVENT","END:VCALENDAR`].join("\r\n");
  const a = Object.assign(document.createElement("a"),{href:URL.createObjectURL(new Blob([ics],{type:"text/calendar"})),download:`${ev.title.replace(/[^a-z0-9]+/gi,"-").toLowerCase()}.ics`});
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  toast.success("Calendar file downloaded");
}

// WhatsApp SVG icon
function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

// X/Twitter SVG icon
function XIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );
}

export function AddToCalendar({ event }) {
  const [copied, setCopied] = useState(false);
  const gUrl = googleCalUrl(event);

  const shareUrl = `${window.location.origin}${window.location.pathname}#events`;
  const shareText = `${event.title} — ${event.date ? new Date(event.date+"T00:00:00").toLocaleDateString(undefined,{month:"short",day:"numeric"}) : ""} in ${event.city||"NC"}. Check it out on NC Desi!`;

  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText + "\n" + shareUrl)}`;
  const xUrl  = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;

  function copyLink() {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="mt-4 flex gap-2">
      {/* Add to Calendar dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="rounded-full flex-1 text-xs">
            <CalendarCheck className="w-3.5 h-3.5 mr-1.5 text-primary" />Add to Calendar
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          {gUrl && (
            <DropdownMenuItem asChild>
              <a href={gUrl} target="_blank" rel="noreferrer" className="cursor-pointer">
                <ExternalLink className="w-4 h-4 mr-2" />Google Calendar
              </a>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => downloadIcs(event)} className="cursor-pointer">
            <Download className="w-4 h-4 mr-2" />Apple / Outlook (.ics)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Share dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="rounded-full flex-1 text-xs">
            <Share2 className="w-3.5 h-3.5 mr-1.5 text-primary" />Share
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem asChild>
            <a href={waUrl} target="_blank" rel="noreferrer" className="cursor-pointer flex items-center gap-2 text-[#25D366]">
              <WhatsAppIcon />WhatsApp
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a href={xUrl} target="_blank" rel="noreferrer" className="cursor-pointer flex items-center gap-2">
              <XIcon />Post on X
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={copyLink} className="cursor-pointer">
            {copied ? <Check className="w-4 h-4 mr-2 text-green-500" /> : <Link2 className="w-4 h-4 mr-2" />}
            {copied ? "Copied!" : "Copy link"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
